import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { MapRef } from '@vis.gl/react-maplibre';
import {
  applyNeutralBackdrop,
  setBasemap,
  transformGeoJsonToLatLon,
} from 'utils/maplibre';
import { utmToLatLon } from 'utils/utmToLatLon';
import { useAppDispatch, useAppSelector } from 'store/hook';
import CustomerShiftConfigAPI from 'api/customer-shift-config.api';
import {
  setCustomerShiftConfigData,
  setCustomerShiftConfigLoading,
} from 'store/customer-shift-config.slice';

export const CAR_CUSTOMER_CODE = 'car';
export const MYN_CUSTOMER_CODE = 'myn';
export const BUZ_CUSTOMER_CODE = 'buz';
export const DRN_CUSTOMER_CODE = 'drn';

// Customers with no contour-line overlay of their own but who still need the
// plain neutral backdrop (no imagery tiles) rather than default satellite
// imagery — see the rationale in the useSiteRoadOverlay doc comment below.
const NEUTRAL_BACKDROP_ONLY_CUSTOMER_CODES = new Set([DRN_CUSTOMER_CODE]);

// Every customer with no usable real-tile coverage at all — drn (flat
// backdrop only) plus myn/buz (flat backdrop + their own contour overlay).
// Any screen that decides its basemap declaratively or imperatively without
// going through useSiteRoadOverlay (e.g. a location-picker modal that has no
// site overlay of its own) must still route through this check rather than
// requesting real satellite/neutral-road tiles for these customers — see the
// NEUTRAL_BACKDROP_COLOR/applyNeutralBackdrop rationale in utils/maplibre.ts.
const NO_SATELLITE_COVERAGE_CUSTOMER_CODES = new Set([
  DRN_CUSTOMER_CODE,
  MYN_CUSTOMER_CODE,
  BUZ_CUSTOMER_CODE,
]);

export const hasNoSatelliteCoverage = (customerCode: string): boolean =>
  NO_SATELLITE_COVERAGE_CUSTOMER_CODES.has(customerCode);

export const CAR_ROAD_GEOJSON_PATH = '/geojson/my-layer.geojson';
export const CAR_ROAD_LAYER_IDS = new Set(['1003']);

export const MYN_GEOJSON_PATH = '/geojson/myn.geojson';
export const MYN_STATE_PLANE_PROJ4 =
  '+proj=lcc +lat_0=31.6666666666667 +lon_0=-98.5 +lat_1=33.9666666666667 +lat_2=32.1333333333333 +x_0=600000 +y_0=2000000.0001016 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=us-ft +no_defs';

export const BUZ_GEOJSON_PATH =
  '/geojson/Cape_Girardeau_Topo_2025-10-02_ACAD2010_converted.geojson';
export const BUZ_STATE_PLANE_PROJ4 =
  '+proj=tmerc +lat_0=35.8333333333333 +lon_0=-90.5 +k=0.999933333 +x_0=250000.0001016 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=us-ft +no_defs';

// Customers whose site is rendered as a contour-line overlay (geojson in a
// local/state-plane CRS, reprojected via a proj4 definition).
export const CONTOUR_GEOJSON_PATH_BY_CUSTOMER: Record<string, string> = {
  [MYN_CUSTOMER_CODE]: MYN_GEOJSON_PATH,
  [BUZ_CUSTOMER_CODE]: BUZ_GEOJSON_PATH,
};

// Customers whose customer record has no usable proj4String, so the contour
// overlay reprojects with a hardcoded definition instead.
export const CONTOUR_STATE_PLANE_PROJ4_BY_CUSTOMER: Record<string, string> = {
  [MYN_CUSTOMER_CODE]: MYN_STATE_PLANE_PROJ4,
  [BUZ_CUSTOMER_CODE]: BUZ_STATE_PLANE_PROJ4,
};

export const CONTOUR_LAYER_STYLES: Record<
  string,
  { stroke: string; strokeWidth: number }
> = {
  Contour_Line_Major: { stroke: '#8a5a2b', strokeWidth: 1.5 },
  Contour_Line_Intermediate: { stroke: '#c9a774', strokeWidth: 0.75 },
};

export const filterCarRoadFeatures = (geoJson: any) => {
  return {
    ...geoJson,
    features: (geoJson?.features || []).filter((feature: any) =>
      CAR_ROAD_LAYER_IDS.has(String(feature?.properties?.layer ?? ''))
    ),
  };
};

export const styleContourFeatures = (geoJson: any) => ({
  ...geoJson,
  features: (geoJson?.features || []).map((feature: any) => {
    const layer = feature?.properties?.Layer;
    const style =
      CONTOUR_LAYER_STYLES[layer] ??
      CONTOUR_LAYER_STYLES.Contour_Line_Intermediate;
    return {
      ...feature,
      properties: {
        ...feature.properties,
        stroke: style.stroke,
        'stroke-width': style.strokeWidth,
        'stroke-opacity': 1,
      },
    };
  }),
});

export interface SiteOverlayProj4Config {
  proj4String?: string | null;
  localOriginEasting?: number | null;
  localOriginNorthing?: number | null;
}

// Shared across all useSiteRoadOverlay instances so multiple map panels
// mounted at once for the same customer (e.g. several FatigueMap panels on
// one dashboard) fetch each GeoJSON file only once.
const geoJsonFetchCache = new Map<string, Promise<any>>();

export const fetchGeoJsonCached = async (path: string): Promise<any> => {
  let cached = geoJsonFetchCache.get(path);
  if (!cached) {
    cached = fetch(path).then(async (response) => await response.json());
    cached.catch(() => geoJsonFetchCache.delete(path));
    geoJsonFetchCache.set(path, cached);
  }
  return await cached;
};

const OVERLAY_SOURCE_ID = 'site-road-overlay';
const OVERLAY_LINE_LAYER_ID = 'site-road-overlay-line';

const removeOverlayLayer = (map: MapLibreMap) => {
  if (map.getLayer(OVERLAY_LINE_LAYER_ID))
    map.removeLayer(OVERLAY_LINE_LAYER_ID);
  if (map.getSource(OVERLAY_SOURCE_ID)) map.removeSource(OVERLAY_SOURCE_ID);
};

export interface LonLatBounds {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
}

// Walks every coordinate in a GeoJSON FeatureCollection to find its lon/lat
// bounding box — used so the initial camera fit can include the FULL site
// extent (not just wherever the trucks happen to be clustered right now).
const computeLonLatBounds = (geojson: any): LonLatBounds | null => {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  const visit = (coords: unknown): void => {
    if (
      Array.isArray(coords) &&
      typeof coords[0] === 'number' &&
      typeof coords[1] === 'number'
    ) {
      const [lon, lat] = coords;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      return;
    }
    if (Array.isArray(coords)) coords.forEach(visit);
  };

  for (const feature of geojson?.features ?? []) {
    visit(feature?.geometry?.coordinates);
  }

  return Number.isFinite(minLon) ? { minLon, maxLon, minLat, maxLat } : null;
};

const addOverlayLineLayer = (
  map: MapLibreMap,
  geojson: unknown,
  paint: { color: string; width: number; opacity?: number }
) => {
  removeOverlayLayer(map);
  map.addSource(OVERLAY_SOURCE_ID, { type: 'geojson', data: geojson as any });
  map.addLayer({
    id: OVERLAY_LINE_LAYER_ID,
    type: 'line',
    source: OVERLAY_SOURCE_ID,
    paint: {
      // Cesium's GeoJsonDataSource read each feature's own stroke/
      // stroke-width/stroke-opacity (simplestyle-spec) automatically —
      // styleContourFeatures still sets those per-feature so major vs
      // intermediate contour lines render differently, but a MapLibre line
      // layer only picks them up via an explicit ['get', ...] expression.
      // Falling back to the flat `paint` argument keeps callers whose
      // features DON'T carry those properties (the car-road overlay)
      // rendering exactly as before.
      'line-color': ['coalesce', ['get', 'stroke'], paint.color],
      'line-width': ['coalesce', ['get', 'stroke-width'], paint.width],
      'line-opacity': [
        'coalesce',
        ['get', 'stroke-opacity'],
        paint.opacity ?? 1,
      ],
    },
  });
};

// Applies the shared "no satellite imagery here" site treatment to a MapLibre
// map, for any screen that renders one: customers with the backend-driven
// showNaturalroad flag get a neutral road basemap plus the car road-network
// GeoJSON overlay; myn/buz get a plain neutral backdrop (no imagery tiles at
// all — Esri returns a valid "Map data not yet available" placeholder tile
// once you're past its real coverage for these remote sites, which no
// zoom-distance limit can fully avoid) plus their contour-line GeoJSON
// overlay; drn gets the same neutral backdrop with no contour overlay; every
// other customer keeps the default satellite imagery. Pass any extra values
// the overlay should re-apply on (e.g. a trip id) via extraDeps.
export const useSiteRoadOverlay = (
  mapRef: MutableRefObject<MapRef | null>,
  customerCode: string,
  proj4Config: SiteOverlayProj4Config,
  extraDeps: unknown[] = []
) => {
  const overlayAppliedRef = useRef(false);
  const { proj4String, localOriginEasting, localOriginNorthing } = proj4Config;
  const showNaturalroad = useAppSelector(
    (state) => state.customerShiftConfigReducer.data?.showNaturalroad ?? false
  );
  // The car-road/contour overlay's own full extent — callers use this to
  // frame the initial camera on the WHOLE site rather than just wherever
  // the live assets happen to be clustered right now.
  const [siteBounds, setSiteBounds] = useState<LonLatBounds | null>(null);

  const dispatch = useAppDispatch();
  const shiftConfigCustCode = useAppSelector(
    (state) => state.customerShiftConfigReducer.custCode
  );
  // Whether we actually know this customer's real showNaturalroad value yet.
  // customerShiftConfigReducer starts empty on a fresh load (see the fetch
  // effect below), so `showNaturalroad` reads its `false` default until the
  // fetch for THIS customerCode resolves — treating that default as ground
  // truth made every natural-road customer flash real satellite tiles on
  // load before flipping to the road view a moment later.
  const isShiftConfigReady = shiftConfigCustCode === customerCode;
  // customerShiftConfigReducer isn't persisted (see store/index.ts's persist
  // whitelist) and is otherwise only ever populated by CustomerSelector or a
  // couple of ScheduleReport screens — a plain page refresh on a screen that
  // uses this hook, or landing here without passing through one of those,
  // leaves showNaturalroad at its `false` default forever, which sends CAR
  // straight to the satellite fallback below with nothing to correct it.
  // Fetching it here whenever it's missing for the active customer makes
  // every consumer of this hook self-sufficient instead of depending on
  // unrelated screens to have warmed this state first.
  const fetchedShiftConfigCustomerRef = useRef<string | null>(null);
  useEffect(() => {
    if (shiftConfigCustCode === customerCode) return;
    if (fetchedShiftConfigCustomerRef.current === customerCode) return;
    fetchedShiftConfigCustomerRef.current = customerCode;

    let cancelled = false;
    dispatch(setCustomerShiftConfigLoading(true));

    CustomerShiftConfigAPI.getCustomerShiftConfig(customerCode)
      .then((data) => {
        if (cancelled) return;
        dispatch(setCustomerShiftConfigData({ custCode: customerCode, data }));
      })
      .catch((error: unknown) => {
        console.error('Failed to load customer shift config', error);
        if (!cancelled) {
          dispatch(setCustomerShiftConfigLoading(false));
          fetchedShiftConfigCustomerRef.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [customerCode, shiftConfigCustCode, dispatch]);

  useEffect(() => {
    let cancelled = false;

    const applyOverlay = async (map: MapLibreMap) => {
      if (cancelled) return;

      // Don't guess satellite before we know this customer's real
      // showNaturalroad value — a flat neutral backdrop is a safe "no
      // imagery yet" placeholder that matches the eventual road-view
      // default instead of flashing real (network-fetched) satellite tiles
      // that get replaced a moment later once the config loads.
      if (!isShiftConfigReady) {
        applyNeutralBackdrop(map);
        return;
      }

      setBasemap(map, 'satellite');
      removeOverlayLayer(map);
      overlayAppliedRef.current = false;

      const contourGeoJsonPath = CONTOUR_GEOJSON_PATH_BY_CUSTOMER[customerCode];

      if (showNaturalroad && !contourGeoJsonPath) {
        // Esri's neutral-road canvas layer has real coverage for this site
        // near where the road network was surveyed, but it's PATCHY, not a
        // clean "below zoom X" cutoff — probing one point and capping zoom
        // uniformly still let the placeholder show up elsewhere in the
        // viewport once the full site (rather than a tight crop) was in
        // view. A flat neutral backdrop requests no raster tiles at all, so
        // there's nothing left that can ever show "no data" — same
        // treatment myn/buz/drn already use without this problem.
        applyNeutralBackdrop(map);

        try {
          const geoJson = await fetchGeoJsonCached(CAR_ROAD_GEOJSON_PATH);
          if (cancelled) return;
          const roadGeoJson = filterCarRoadFeatures(geoJson);
          addOverlayLineLayer(map, roadGeoJson, {
            color: '#F5A623',
            width: 1.25,
            opacity: 0.72,
          });
          overlayAppliedRef.current = true;
          if (!cancelled) setSiteBounds(computeLonLatBounds(roadGeoJson));
        } catch (error) {
          console.error('Failed to load car road GeoJSON overlay', error);
        }
        return;
      }

      if (contourGeoJsonPath) {
        applyNeutralBackdrop(map);

        try {
          const geoJson = await fetchGeoJsonCached(contourGeoJsonPath);
          if (cancelled) return;
          const styledGeoJson = styleContourFeatures(geoJson);
          const hardcodedProj4 =
            CONTOUR_STATE_PLANE_PROJ4_BY_CUSTOMER[customerCode];
          const overlayProj4String = hardcodedProj4 ?? proj4String;
          const overlayLocalOriginEasting = hardcodedProj4
            ? 0
            : localOriginEasting;
          const overlayLocalOriginNorthing = hardcodedProj4
            ? 0
            : localOriginNorthing;
          const transformedGeoJson = transformGeoJsonToLatLon(
            styledGeoJson,
            (x, y) => {
              const latLon = utmToLatLon(
                x,
                y,
                overlayProj4String,
                overlayLocalOriginEasting,
                overlayLocalOriginNorthing
              );
              return latLon
                ? { lat: latLon.latitude, lon: latLon.longitude }
                : null;
            }
          );
          if (cancelled) return;
          addOverlayLineLayer(map, transformedGeoJson, {
            color: '#8a5a2b',
            width: 1.5,
          });
          overlayAppliedRef.current = true;
          if (!cancelled) {
            setSiteBounds(computeLonLatBounds(transformedGeoJson));
          }
        } catch (error) {
          console.error('Failed to load contour GeoJSON overlay', error);
        }
        return;
      }

      if (NEUTRAL_BACKDROP_ONLY_CUSTOMER_CODES.has(customerCode)) {
        applyNeutralBackdrop(map);
        return;
      }

      setBasemap(map, 'satellite');
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let readyListenerMap: MapLibreMap | null = null;

    const applyWhenReady = () => {
      if (cancelled) return;
      const map = mapRef.current?.getMap();

      if (map?.isStyleLoaded()) {
        applyOverlay(map).catch(() => {});
        return;
      }

      if (map) {
        // Map instance exists but its style hasn't finished loading — wait
        // for the real 'load' event instead of polling with a bounded
        // budget. A fixed attempt count (previously 10x200ms) can be beaten
        // by a slow cold app boot (auth/customer/asset fetches all
        // competing for the main thread on first load — see fetch.all.ts),
        // and gave up SILENTLY with no fallback, leaving the overlay never
        // applied for the rest of the session even after everything
        // settled. A warm reload not competing with that boot sequence
        // would finish in time and look "fixed", which is exactly the
        // "works after hard refresh" symptom this caused.
        readyListenerMap = map;
        void map.once('load', applyWhenReady);
        return;
      }

      // mapRef isn't attached to a map instance yet — there's no event to
      // wait on for this case, so it's still a bounded poll, just generous
      // enough to survive a slow cold boot (~15s) rather than give up at 2s.
      if (attempts >= 75) return;
      attempts += 1;
      timer = setTimeout(applyWhenReady, 200);
    };

    timer = setTimeout(applyWhenReady, 100);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      readyListenerMap?.off('load', applyWhenReady);
      const map = mapRef.current?.getMap();
      if (map && overlayAppliedRef.current) {
        try {
          removeOverlayLayer(map);
        } catch {
          // map already torn down
        }
        overlayAppliedRef.current = false;
      }
      setSiteBounds(null);
    };
  }, [
    mapRef,
    customerCode,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
    showNaturalroad,
    isShiftConfigReady,
    ...extraDeps,
  ]);

  return { siteBounds };
};
